'use client';

import { useEffect, useRef } from 'react';

/**
 * Prevents browser navigation (back/forward, tab close) while active.
 *
 * Intercepts `popstate` and immediately reverses the navigation, then calls
 * `onBlock` so the consumer can show a confirmation UI or close a sheet.
 *
 * Call `release()` before programmatic navigation to tear down the guard.
 */
export const useNavigationGuard = (shouldBlock: boolean, onBlock?: () => void): {
  release: () => void;
} => {
  const onBlockRef = useRef(onBlock);
  onBlockRef.current = onBlock;
  const activeRef = useRef(false);
  const didPushRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);
  const release = () => {
    activeRef.current = false;
    cleanupRef.current?.();
    cleanupRef.current = null;
    // Pop the entry we pushed to reverse a back-press (if any).
    if (didPushRef.current) {
      didPushRef.current = false;
      globalThis.history.back();
    }
  };
  useEffect(() => {
    if (!shouldBlock) {
      activeRef.current = false;
      if (didPushRef.current) {
        didPushRef.current = false;
        globalThis.history.back();
      }
      cleanupRef.current = null;
      return;
    }
    activeRef.current = true;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    const handlePopState = () => {
      if (!activeRef.current) {
        return;
      }

      // The browser already navigated (back or forward). Push a new entry
      // at the current URL to "undo" it. Since popstate popped one and we
      // push one, the stack depth stays the same.
      globalThis.history.pushState(null, '');
      didPushRef.current = true;
      if (onBlockRef.current) {
        onBlockRef.current();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    globalThis.addEventListener('popstate', handlePopState);
    cleanupRef.current = () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.removeEventListener('popstate', handlePopState);
    };
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.removeEventListener('popstate', handlePopState);
      cleanupRef.current = null;
    };
  }, [shouldBlock]);
  return {
    release
  };
};